import { create } from "zustand";
import { produce } from "immer";
import { supabase } from "@/lib/supabase";
import { Logger } from "@/lib/logger/Logger";
import {
	QuickStartTaskId,
	TOTAL_QUICKSTART_TASKS,
} from "../../pages/quickstart/quickstart-tasks";
import {
	QuickStartAttributeStatus,
	QuickStartAttributeValue,
	quickstartValueToStatus,
	quickstartStatusToValue,
	OnboardingAttributeStatus,
	OnboardingAttributeValue,
	onboardingValueToStatus,
	onboardingStatusToValue,
} from "./attributes-mappings";
import { useCentralStore } from "../Central";
import { quickStartTaskIsCompleted } from "./helpers";

export type AttributeScope = "profile" | "org";

interface AttributesState {
	attributes: Record<string, number>;
	loadAttributes: (userId: string, organizationId: string) => Promise<void>;
	getAttribute: (key: string) => number | undefined;
	setAttribute: (
		key: string,
		value: number,
		scope: AttributeScope
	) => Promise<void>;
	deleteAttribute: (key: string, scope: AttributeScope) => Promise<void>;
	setQuickstartAttribute: (
		taskId: QuickStartTaskId,
		value: QuickStartAttributeValue,
		scope?: AttributeScope
	) => Promise<void>;
	getQuickstartTaskStatuses: () => Record<
		QuickStartTaskId,
		QuickStartAttributeValue
	>;
	getQuickstartProgress: () => {
		completed: number;
		total: number;
		percentage: number;
	};
	getQuickstartCompletedTaskIds: () => QuickStartTaskId[];
	setOnboardingAttribute: (
		stepKey: string,
		value: OnboardingAttributeValue,
		scope?: AttributeScope
	) => Promise<void>;
	getOnboardingAttribute: (
		stepKey: string
	) => OnboardingAttributeValue | undefined;
	hasCompletedOnboarding: () => boolean;
}

export const useAttributesStore = create<AttributesState>((set, get) => ({
	attributes: {},

	loadAttributes: async (userId: string, organizationId: string) => {
		const { data, error } = await supabase
			.from("attributes_view")
			.select("key, value")
			.or(`profile_id.eq.${userId},organization_id.eq.${organizationId}`);

		if (error) {
			Logger.error("Failed to load attributes: " + String(error));
			return;
		}

		const attributesMap: Record<string, number> = {};
		data?.forEach((attr) => {
			if (attr.key && attr.value) {
				attributesMap[attr.key] = attr.value;
			}
		});

		set({ attributes: attributesMap });
		Logger.log("Attributes loaded", attributesMap);
	},
	getAttribute: (key: string) => {
		return get().attributes[key];
	},
	setAttribute: async (key: string, value: number, scope: AttributeScope) => {
		if (scope === "profile") {
			const profileId = useCentralStore.getState().profile?.id;
			if (!profileId) {
				Logger.error("No profile ID when setting profile attribute");
				return;
			}
			const { error } = await supabase
				.from("attributes_profile")
				.upsert(
					{ profile_id: profileId, key, value },
					{ onConflict: "profile_id,key" }
				);

			if (error) {
				Logger.error(
					"Failed to set profile attribute: " + String(error)
				);
				return;
			}
		} else {
			const organizationId = useCentralStore.getState().organization?.id;
			if (!organizationId) {
				Logger.error("No organization ID when setting org attribute");
				return;
			}
			const { error } = await supabase
				.from("attributes_org")
				.upsert(
					{ organization_id: organizationId, key, value },
					{ onConflict: "organization_id,key" }
				);

			if (error) {
				Logger.error("Failed to set org attribute: " + String(error));
				return;
			}
		}

		set(
			produce((state) => {
				state.attributes[key] = value;
			})
		);

		Logger.log(`Attribute set: ${key} = ${value} (${scope})`);
	},
	deleteAttribute: async (key: string, scope: AttributeScope) => {
		if (scope === "profile") {
			const profileId = useCentralStore.getState().profile?.id;
			if (!profileId) {
				Logger.error("No profile ID when deleting profile attribute");
				return;
			}
			const { error } = await supabase
				.from("attributes_profile")
				.delete()
				.eq("profile_id", profileId)
				.eq("key", key);

			if (error) {
				Logger.error(
					"Failed to delete profile attribute: " + String(error)
				);
				return;
			}
		} else {
			const organizationId = useCentralStore.getState().organization?.id;
			if (!organizationId) {
				Logger.error("No organization ID when deleting org attribute");
				return;
			}
			const { error } = await supabase
				.from("attributes_org")
				.delete()
				.eq("organization_id", organizationId)
				.eq("key", key);

			if (error) {
				Logger.error(
					"Failed to delete org attribute: " + String(error)
				);
				return;
			}
		}

		set(
			produce((state) => {
				delete state.attributes[key];
			})
		);

		Logger.log(`Attribute deleted: ${key} (${scope})`);
	},
	setQuickstartAttribute: async (
		taskId: QuickStartTaskId,
		value: QuickStartAttributeValue,
		scope?: AttributeScope
	) => {
		const effectiveScope = scope ?? "org";
		const numericValue = quickstartValueToStatus(value);

		await get().setAttribute(taskId, numericValue, effectiveScope);
	},

	getQuickstartTaskStatuses: () => {
		const attributes = get().attributes;
		const statuses: Record<string, QuickStartAttributeValue> = {};

		Object.entries(attributes).forEach(([key, value]) => {
			if (
				key.startsWith("quickstart-") &&
				value in QuickStartAttributeStatus
			) {
				statuses[key] = quickstartStatusToValue(
					value as QuickStartAttributeStatus
				);
			}
		});

		return statuses as Record<QuickStartTaskId, QuickStartAttributeValue>;
	},
	getQuickstartProgress: () => {
		const taskStatuses = get().getQuickstartTaskStatuses();
		const completed = Object.values(taskStatuses).filter((status) =>
			quickStartTaskIsCompleted(status)
		).length;
		const percentage =
			TOTAL_QUICKSTART_TASKS > 0
				? Math.round((completed / TOTAL_QUICKSTART_TASKS) * 100)
				: 0;

		return { completed, total: TOTAL_QUICKSTART_TASKS, percentage };
	},
	getQuickstartCompletedTaskIds: () => {
		const taskStatuses = get().getQuickstartTaskStatuses();
		const completedTaskIds = Object.entries(taskStatuses)
			.filter(([_, status]) => quickStartTaskIsCompleted(status))
			.map(([taskId]) => taskId as QuickStartTaskId);

		return completedTaskIds;
	},
	setOnboardingAttribute: async (
		stepKey: string,
		value: OnboardingAttributeValue,
		scope?: AttributeScope
	) => {
		const effectiveScope = scope ?? "org";
		const numericValue = onboardingValueToStatus(value);

		await get().setAttribute(stepKey, numericValue, effectiveScope);
	},
	getOnboardingAttribute: (stepKey: string) => {
		const numericValue = get().attributes[stepKey];
		if (numericValue === undefined) return undefined;

		return onboardingStatusToValue(
			numericValue as OnboardingAttributeStatus
		);
	},
	hasCompletedOnboarding: () => {
		const migrationDecision = get().getOnboardingAttribute(
			"onboarding_migration_decision"
		);
		if (
			migrationDecision === "migrate_from_dlxp" ||
			migrationDecision === "migration_done"
		) {
			return true;
		}

		if (migrationDecision === "no_migration") {
			return true;
		}

		const priceListStatus = get().getOnboardingAttribute(
			"onboarding_price_list"
		);
		return priceListStatus === "completed";
	},
}));
